import React from 'react';
import Select from 'react-select';
import DatePicker from 'react-widgets/DatePicker';

import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';

import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';

import 'react-widgets/styles.css';

import 'styles/search/DateSearchField.styl';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

const dsfo = ConstValues.datetimeSearchFieldsOptions;
const dwsf = ConstValues.datetimeWithinSearchFieldsOptions;
const dsvt = ConstValues.datetimeSearchValueTypes;

const SELECT_OPTIONS = [
  {value: dsfo.dateBetween, label: 'Date Between...'},
  {value: dsfo.exactDate, label: 'Exact Date'},
  {value: dsfo.beforeDate, label: 'Before Date'},
  {value: dsfo.within, label: 'Last...'},
];

const DATE_WITHIN_SELECT_OPTIONS = [
  {value: dwsf.withinTheLastSevenDays, label: '...7 days'},
  {value: dwsf.withinTheLast30Days, label: '...30 days'},
  {value: dwsf.withinTheLast90Days, label: '...90 days'},
];

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 999999,
  }),
};

/**
 * A `DateSearchField` renders a material-ui "Drop Down Menu" which contains
 * four different types of date based widget selection:
 * - Date Between...
 * - Exact Date
 * - Before Date
 * - Within...
 */
@componentHoc
export default class DateSearchField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toggled: false,
    };
    this.onToDatetimeChange = this.onToDatetimeChange.bind(this);
    this.onFromDatetimeChange = this.onFromDatetimeChange.bind(this);
    this.onOneDatetimeChange = this.onOneDatetimeChange.bind(this);
    this.onWithinDatetimeChange = this.onWithinDatetimeChange.bind(this);
    this.onSelectOptionChange = this.onSelectOptionChange.bind(this);
    this.onToggleDateSearchWidget = this.onToggleDateSearchWidget.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (typeof nextProps.toggled !== 'undefined') {
      this.setState({
        toggled: nextProps.toggled,
      });
    }
  }

  /**
   * Callback function for the toggle of the date search widget
   * @param {object} event An event object
   * @param {boolean} toggled A boolean indicating whether the search widget is toggled or not
   */
  onToggleDateSearchWidget(event, toggled) {
    const {searchField} = this.props;
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.datetimeWidgetType,
      toggled,
      searchField
    );

    setTimeout(() => {
      SearchFieldsActions.updateDatetimeSearchExpression(searchField, toggled);
    }, ConstValues.delayDurationForSearchWidgetToggles);

    if (toggled) {
      this.props.updateLastToggledOnSearchWidget(searchField.name);
    }
  }

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls to the widget if it is the most recent Search Widget to be toggled on.
   */
  componentDidUpdate() {
    if (this.props.shouldFocus) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // Reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  onSelectOptionChange(newValue) {
    this.props.onSelectOptionChange(this.props.searchField, newValue?.value);
  }

  onFromDatetimeChange(date, value) {
    date = new Date(new Date(date).setHours(0, 0, 0, 0));
    this.props.onDatetimeChange(this.props.searchField, date, dsvt.from);
  }

  onToDatetimeChange(date, value) {
    date = new Date(new Date(date).setHours(23, 59, 59, 999));
    this.props.onDatetimeChange(this.props.searchField, date, dsvt.to);
  }

  renderDateBetween = () => {
    const {dateValue} = this.props;
    let lte = null;
    let gte = null;
    if (dateValue) {
      const dv = dateValue;
      if (dv.lte) {
        lte = new Date(new Date(dv.lte).setHours(23, 59, 59, 999));
      }
      if (dv.gte) {
        gte = new Date(new Date(dv.gte).setHours(0, 0, 0, 0));
      }
    }
    return (
      <div>
        <DatePicker
          placeholder={`From...`}
          value={gte}
          className="datetime-first-picker"
          onChange={this.onFromDatetimeChange}
        />
        <div className="datetime-center-label">
          <span>and</span>
        </div>
        <DatePicker placeholder={`To...`} value={lte} onChange={this.onToDatetimeChange} />
      </div>
    );
  };

  onOneDatetimeChange(date, value) {
    if (this.props.value === dsfo.exactDate) {
      this.props.onDatetimeChange(this.props.searchField, date, dsvt.exact);
    } else if (this.props.value === dsfo.beforeDate) {
      this.props.onDatetimeChange(this.props.searchField, date, dsvt.before);
    }
  }

  onWithinDatetimeChange(newValue) {
    this.props.onSelectedWithinDatetimeChange(this.props.searchField, dsfo.within, newValue?.value);
    if (newValue?.value === '') {
      this.props.removeDatetimeSearchExpression(this.props.searchField);
    } else {
      const ndt = getWithinDatetime(new Date(), newValue?.value);
      this.props.onDatetimeChange(this.props.searchField, ndt, dsvt.within);
    }
  }

  renderOneDatePicker = () => {
    let value = null;
    if (this.props.dateValue) {
      const dv = this.props.dateValue;
      if (dv.lte) {
        value = new Date(dv.lte);
      } else if (dv.lt) {
        value = new Date(dv.lt);
      } else if (dv.gte) {
        value = new Date(dv.gte);
      }
    }
    return (
      <div>
        <DatePicker
          value={value}
          placeholder={`Type or pick date`}
          className="datetime-first-picker"
          onChange={this.onOneDatetimeChange}
        />
      </div>
    );
  };

  renderWithin = () => {
    return (
      <div>
        <Select
          styles={customStyles}
          multi={false}
          searchable={false}
          name="date-within-dropdown"
          options={DATE_WITHIN_SELECT_OPTIONS}
          value={DATE_WITHIN_SELECT_OPTIONS.find(el => el.value === this.props.dateWithinValue)}
          className="datetime-first-picker"
          onChange={this.onWithinDatetimeChange}
        />
      </div>
    );
  };

  renderDateWidget() {
    const {value} = this.props;
    return (
      <div className="date-search-field">
        <Select
          styles={customStyles}
          placeholder="Select Period"
          multi={false}
          searchable={false}
          name="date-search-field-dropdown"
          options={SELECT_OPTIONS}
          onChange={this.onSelectOptionChange}
          value={SELECT_OPTIONS.find(el => el.value === value)}
        />
        {{
          [dsfo.dateBetween]: this.renderDateBetween,
          [dsfo.exactDate]: this.renderOneDatePicker,
          [dsfo.beforeDate]: this.renderOneDatePicker,
          [dsfo.within]: this.renderWithin,
        }[value]?.() || null}
      </div>
    );
  }

  render() {
    const {toggled} = this.state;
    const classes = cn('search-widget', getSearchFieldClass(this.props.searchField.name));
    return (
      <div className={classes}>
        <Toggle
          label={this.props.searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleDateSearchWidget}
          toggled={this.state.toggled}
        />
        <Divider style={{marginTop: '5px'}} />
        {toggled && this.renderDateWidget()}
      </div>
    );
  }
}

/**
 * Return a Date object in the past that's within `value` days from `dt`
 * @param  {Date} dt    A `Date` object
 * @param  {string} value A `ConstValues.datetimeSearchValueTypes` string value
 * @return {Date}       A new `Date` object in the past
 */
function getWithinDatetime(dt, value) {
  const ndt = new Date(dt.getTime());
  ndt.setDate(ndt.getDate() - parseInt(value));
  return ndt;
}
